/**
 * How the screen moves as you go somewhere (SPEC §7.10). Tabs slide sideways in the tab bar's
 * order while the bar stays and the line under it glides along; Home · Notes · Pages · Chat slide
 * the same way, in the phone bar's order; a page comes in from the right over where you were and
 * goes back out to the right; a card's own page grows out of the card's bar, and shrinks back
 * into it. Built on View Transitions: where there are none, with Reduce motion on, or mid-drag,
 * the screen just changes.
 */
import { flushSync } from 'react-dom'
import { ancestorsOf } from '@terrazzo/core'
import { store } from '../store/store'
import { gestureState } from '../store/ui'
import type { Route } from './router'

type Move = 'tab-next' | 'tab-prev' | 'next' | 'prev' | 'push' | 'pop' | 'open-card' | 'close-card'

/** Top-level places, in the phone bar's order (then the ones reached from the hold-+ box). */
const PLACES = ['tabs', 'notes', 'pages', 'search', 'chat', 'activity', 'settings']

function place(r: Route): string | null {
  switch (r.name) {
    case 'home':
    case 'board':
    case 'days':
    case 'done':
    case 'view':
      return 'tabs'
    case 'item':
    case 'notes':
    case 'pages':
    case 'search':
    case 'chat':
    case 'activity':
    case 'settings':
      return r.name
    default:
      return null
  }
}

/** How deep a screen is: a page one more than the pages and card it's inside. */
function depth(r: Route): number {
  if (r.name === 'item') return 1 + ancestorsOf(store.tree, r.id).length
  if (r.name === 'settings') return r.section ? 1 : 0
  return 0
}

/** Where a tab sits in the tab bar, as drawn (All · Days · your tabs · Done). */
function tabAt(r: Route): number {
  const key = r.name === 'view' ? `view:${r.id}` : r.name
  const tabs = [...document.querySelectorAll<HTMLElement>('[data-tab-header] [data-tab-key]')]
  return tabs.findIndex((t) => (r.name === 'home' ? t.hasAttribute('data-tab-home') : t.dataset.tabKey === key))
}

function decide(from: Route, to: Route, back: boolean): Move | null {
  const a = place(from)
  const b = place(to)
  // Search opens its box with the keyboard up, which phones only do inside the tap itself.
  if (!a || !b || b === 'search') return null
  if (a === 'tabs' && b === 'tabs') {
    const i = tabAt(from)
    const j = tabAt(to)
    return i >= 0 && j >= 0 && j < i ? 'tab-prev' : 'tab-next'
  }
  if (a === b || a === 'item' || b === 'item') {
    const d = depth(to) - depth(from)
    return d > 0 || (d === 0 && !back) ? 'push' : 'pop'
  }
  return PLACES.indexOf(b) < PLACES.indexOf(a) ? 'prev' : 'next'
}

/** A card's bar on screen now, if it's there and in view. */
function cardBar(id: string): HTMLElement | null {
  const bar = document.querySelector<HTMLElement>(`[data-card="${CSS.escape(id)}"] > .card-bar`)
  if (!bar) return null
  const r = bar.getBoundingClientRect()
  return r.bottom > 0 && r.top < innerHeight ? bar : null
}

function canMove(): boolean {
  if (!('startViewTransition' in document) || document.visibilityState !== 'visible' || gestureState.get() !== 'none') return false
  return !matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** A move whose old screen hasn't been taken yet. */
let waiting: ViewTransition | null = null
let latest = 0

/** Something else changed the screen first: show it as is, without the move. */
export function skipMove() {
  waiting?.skipTransition()
  waiting = null
}

/**
 * Go from `from` to `to`: `draw` draws the new screen, then `scroll` puts it where it was (or at
 * the top). `back` for a step back through history; `uaMoved` when the browser already animated
 * it (Safari's edge swipe).
 */
export function moveScreen(from: Route, to: Route, back: boolean, draw: () => void, scroll: () => void, uaMoved = false) {
  const now = () => {
    draw()
    scroll()
  }
  // Another move is still waiting for its first picture: go straight there instead.
  if (waiting) {
    skipMove()
    latest++
    return now()
  }
  let move = !uaMoved && canMove() ? decide(from, to, back) : null
  if (!move) return now()
  const root = document.documentElement
  let named: HTMLElement | null = null
  const name = (el: HTMLElement) => {
    el.style.viewTransitionName = 'page-bar'
    named = el
  }
  if (move === 'push' && to.name === 'item' && from.name !== 'item') {
    const bar = cardBar(to.id)
    if (bar) {
      move = 'open-card'
      name(bar)
    }
  }
  if (move === 'pop' && from.name === 'item' && to.name !== 'item') move = 'close-card'
  root.dataset.move = move
  const n = ++latest
  const t = document.startViewTransition(() => {
    if (waiting === t) waiting = null
    if (n !== latest) return
    flushSync(draw)
    scroll()
    if (move === 'close-card' && from.name === 'item') {
      const bar = cardBar(from.id)
      if (bar) name(bar)
      else root.dataset.move = 'pop'
    }
  })
  waiting = t
  const done = () => {
    if (named) named.style.viewTransitionName = ''
    if (n === latest) delete root.dataset.move
  }
  t.finished.then(done, done)
}
